// © 2025 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

/**
 * When a legacy login stops working — **the single source of that date for the whole web app** (#279).
 *
 * ## Why a constant, and not config or a wire field
 *
 * The deadline is a *product* decision, not tenant data and not deployment configuration: every installation,
 * every company and every environment shows the same day, so there is nothing to configure and nothing to vary.
 * And nothing behavioural keys off it yet — the server neither enforces it nor reports it, so a field on
 * `LegacyClaimStatus` would be a wire contract that only ever carries a constant, plus a mapper, a DTO, a
 * migration of the docs and a test, bought for nothing. (If enforcement ever lands the calculus flips: the
 * enforcing code and the displayed date must then be the *same* value, and this constant should be replaced by
 * a field the backend supplies rather than duplicated beside one. See
 * `docs/articles/authentication/legacy-login-claim.md` § The deadline.)
 *
 * ## If the deadline slips
 *
 * Change the `Date.UTC(...)` literal below and nothing else in the app: the German text is *derived*, not a
 * second literal, and every surface reads it from here. The remaining edits are outside the app and cannot be
 * automated — the two end-user documentation locales
 * (`StaticPages/docs/src/content/docs/{de,en}/verwaltung/erste-schritte/anmelden.mdx`) and their `TRACE.md` row,
 * which state the date in prose.
 *
 * Transitional — deleted together with the legacy authorization system at #264, which is exactly the event this
 * date announces. It lives in `_auth/migration/` for that reason: it dies with the thing it dates.
 */

/**
 * Formats a date the way German UI writes one in running prose — „1. Januar 2027".
 *
 * **Pinned to UTC on purpose.** The deadline is a calendar day, not an instant, and `Intl` renders in the
 * *browser's* zone by default. Left unpinned, a `Date.UTC(2027, 0, 1)` midnight renders as „31. Dezember 2026"
 * for every reader west of Greenwich — a wrong date, in the one sentence on the screen that has to be right.
 *
 * The long form rather than `01.01.2027`: this appears inside a sentence, not in a data column, and a spelled
 * month is the harder of the two to skim past.
 */
export function formatGermanLongDate(date: Date): string {
  return new Intl.DateTimeFormat('de-DE', {day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'})
    .format(date);
}

/**
 * The day the legacy login (username + password) stops working.
 *
 * **The one literal.** Everything else about the deadline is derived from it.
 */
export const LEGACY_LOGIN_DISABLED_ON = new Date(Date.UTC(2027, 0, 1));

/** {@link LEGACY_LOGIN_DISABLED_ON} as the product says it out loud — „1. Januar 2027". */
export const LEGACY_LOGIN_DISABLED_ON_TEXT = formatGermanLongDate(LEGACY_LOGIN_DISABLED_ON);
